import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Award, Clock, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, aboutContent } from '../data/mock';

const APropos = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Header */}
      <section className="py-12 bg-gradient-to-br from-[#0B2A4A]/5 via-white to-[#2F80ED]/5">
        <div className="container mx-auto px-4">
          <Badge className="bg-[#2F80ED] text-white mb-4">À propos</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
            {aboutContent.title}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
            {aboutContent.story}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxwbHVtYmVyfGVufDB8fHx8MTc3MDQwMDEyN3ww&ixlib=rb-4.1.0&q=85"
                alt="Plombier professionnel AquaTherm"
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0B2A4A] mb-6">
                Un artisan passionné à votre service
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Depuis plus de 10 ans, je mets mon expertise au service des particuliers 
                et des professionnels de la région lyonnaise. Ma philosophie : un travail 
                de qualité, des tarifs honnêtes et une relation de confiance avec mes clients.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Chaque intervention est pour moi l'occasion de démontrer mon savoir-faire 
                et mon engagement à votre satisfaction. Je ne me contente pas de réparer, 
                je vous conseille et vous accompagne pour trouver les meilleures solutions.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Badge variant="outline" className="text-[#2F80ED] border-[#2F80ED] px-4 py-2">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {aboutContent.experience}
                </Badge>
                <Badge variant="outline" className="text-[#2F80ED] border-[#2F80ED] px-4 py-2">
                  <Award className="w-4 h-4 mr-2" />
                  Artisan qualifié
                </Badge>
                <Badge variant="outline" className="text-[#2F80ED] border-[#2F80ED] px-4 py-2">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Assurance décennale
                </Badge>
              </div>
              <a href={`tel:${companyInfo.phoneFormatted}`}>
                <Button size="lg" className="bg-[#2F80ED] hover:bg-[#0B2A4A] gap-2 transition-all duration-300">
                  <Phone className="w-5 h-5" />
                  Contactez-moi
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2A4A] mb-4">
              Mes valeurs
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Les principes qui guident mon travail au quotidien
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutContent.values.map((value, index) => {
              const iconMap = [Heart, CheckCircle, Clock, Award];
              const IconComponent = iconMap[index];
              
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#2F80ED] to-[#0B2A4A] rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-lg text-[#0B2A4A] mb-3">
                      {value.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0B2A4A] mb-8 text-center">
              Qualifications & Certifications
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="text-center">
                <CardContent className="p-6">
                  <Award className="w-12 h-12 text-[#2F80ED] mx-auto mb-4" />
                  <h4 className="font-bold text-[#0B2A4A] mb-2">Artisan qualifié</h4>
                  <p className="text-sm text-gray-600">Formation professionnelle certifiée</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <CheckCircle className="w-12 h-12 text-[#2F80ED] mx-auto mb-4" />
                  <h4 className="font-bold text-[#0B2A4A] mb-2">Assurance décennale</h4>
                  <p className="text-sm text-gray-600">Travaux garantis 10 ans</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <CheckCircle className="w-12 h-12 text-[#2F80ED] mx-auto mb-4" />
                  <h4 className="font-bold text-[#0B2A4A] mb-2">Respect des normes</h4>
                  <p className="text-sm text-gray-600">Conformité réglementaire</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#0B2A4A] to-[#2F80ED] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Prêt à travailler ensemble ?
            </h2>
            <p className="text-lg text-gray-100 mb-8">
              Contactez-moi dès maintenant pour discuter de votre projet ou pour un dépannage urgent.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phoneFormatted}`}>
                <Button size="lg" className="bg-white text-[#0B2A4A] hover:bg-gray-100 gap-2 transition-all duration-300">
                  <Phone className="w-5 h-5" />
                  {companyInfo.phone}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0B2A4A] transition-all duration-300">
                  Demander un devis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default APropos;
